/**
 * "Add New Procedure" modal: collects a J-code, its description, and an
 * auth status, validates that all three are filled, and emits the
 * payload via `onSave` for the parent to persist.
 *
 * Auth-status options come from Redux (`state.authStatusOptions`),
 * which is hydrated when the order layout mounts. The in-flight
 * `saving` flag is read from modal `metaData` (set by the parent via
 * `setMetaData({ saving: true })`) so the save button can switch to
 * "Adding..." while the create request is pending.
 *
 * @module AddNewCptCodeModal
 */
import {
  InputField,
  TagOption,
  Tags,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";

/** Payload emitted by {@link AddNewCptCodeModal} on save. */
export interface AddNewCptCodeFormData {
  jCode: string;
  jCodeDescription: string;
  authStatusId: string;
}

/** Props for {@link AddNewCptCodeModal}. */
interface AddNewCptCodeModalProps {
  /** Invoked when the user dismisses the modal. */
  onClose: () => void;
  /**
   * Invoked when the user confirms a fully valid form. Receives the
   * trimmed J-code, description, and selected auth-status id.
   */
  onSave: (data: AddNewCptCodeFormData) => void;
}

/** Tracks per-field user interaction so errors only appear after blur. */
interface TouchedFields {
  jCode: boolean;
  jCodeDescription: boolean;
  authStatusId: boolean;
}

const INITIAL_TOUCHED: TouchedFields = {
  jCode: false,
  jCodeDescription: false,
  authStatusId: false,
};

/**
 * Renders the "Add New Procedure" form. All three fields are required;
 * the save button stays disabled until each has a non-empty value.
 * Inline error messages surface only after a field has been blurred (or
 * after the user attempts to submit an incomplete form).
 */
const AddNewCptCodeModal = ({ onClose, onSave }: AddNewCptCodeModalProps) => {
  const [jCode, setJCode] = useState("");
  const [jCodeDescription, setJCodeDescription] = useState("");
  const [authStatusId, setAuthStatusId] = useState("");
  const [touched, setTouched] = useState<TouchedFields>(INITIAL_TOUCHED);

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { metaData } = useSelector((state: RootState) => state.modal);
  const saving: boolean = Boolean(metaData?.saving);

  const authStatusTagOptions = useMemo<TagOption[]>(
    () =>
      (authStatusOptions ?? []).map((option) => ({
        label: option.text,
        value: option.id,
        backgroundColor: option.bgColor,
        textColor: option.textColor,
      })),
    [authStatusOptions],
  );

  const selectedAuthStatusOption = useMemo(
    () =>
      (authStatusOptions ?? []).find((option) => option.id === authStatusId),
    [authStatusOptions, authStatusId],
  );

  const errors = {
    jCode: !jCode.trim() ? "CPT Code is required" : undefined,
    jCodeDescription: !jCodeDescription.trim()
      ? "Description is required"
      : undefined,
    authStatusId: !authStatusId ? "Auth status is required" : undefined,
  };

  const isFormValid =
    !errors.jCode && !errors.jCodeDescription && !errors.authStatusId;

  const handleSave = () => {
    if (!isFormValid) {
      setTouched({
        jCode: true,
        jCodeDescription: true,
        authStatusId: true,
      });
      return;
    }
    onSave({
      jCode: jCode.trim(),
      jCodeDescription: jCodeDescription.trim(),
      authStatusId,
    });
  };

  return (
    <Modal
      dialogId={ModalId.ADD_NEW_CPT_CODE_MODAL}
      onSave={handleSave}
      onClose={onClose}
      onCancel={onClose}
      title={"Add New Procedure"}
      saveButtonText={saving ? "Adding..." : "Add"}
      cancelText={"Cancel"}
      disableSave={!isFormValid || saving}
    >
      <div className="add-new-cpt-code-modal flex flex-col gap-4">
        <InputField
          id="j-code"
          label="CPT Code"
          size="md"
          fullWidth
          required
          placeholder="Enter CPT-code"
          defaultValue={jCode}
          error={touched.jCode ? errors.jCode : undefined}
          onChange={(e) => setJCode(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, jCode: true }))}
        />
        <InputField
          id="j-code-description"
          label="Description"
          size="md"
          fullWidth
          required
          placeholder="Enter description"
          defaultValue={jCodeDescription}
          error={touched.jCodeDescription ? errors.jCodeDescription : undefined}
          onChange={(e) => setJCodeDescription(e.target.value)}
          onBlur={() =>
            setTouched((prev) => ({ ...prev, jCodeDescription: true }))
          }
        />
        <div className="flex flex-col gap-1">
          <div className="flex flex-row items-center gap-3">
            <div className="text-xs">
              Auth Status
              <span className="text-red-600"> *</span>
            </div>
            <Tags
              label={selectedAuthStatusOption?.text ?? "Select Auth Status"}
              backgroundColor={selectedAuthStatusOption?.bgColor ?? "#F5F5F5"}
              textColor={selectedAuthStatusOption?.textColor ?? "#0F0F0F"}
              options={authStatusTagOptions}
              shouldUsePortal={false}
              onSelect={(option) => {
                setAuthStatusId(option.value);
                setTouched((prev) => ({ ...prev, authStatusId: true }));
              }}
            />
          </div>
          {touched.authStatusId && errors.authStatusId && (
            <div className="text-xs text-red-600">{errors.authStatusId}</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddNewCptCodeModal;
